/**
 * Agents as Tools Tutorial - Beginner (Introduction to Agent Tools)
 *
 * A beginner tutorial that introduces the agents-as-tools pattern.
 * The user creates two specialized translator agents, then builds a workflow
 * with an orchestrator agent that uses those translators as tools.
 *
 * Based on: backend/ai/openai/examples/agents_as_tools.py
 */
import { TutorialStep } from '../../types/tutorial';

const AGENTS_AS_TOOLS_ROUTE_MSG =
  'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
  'Navigate to __Workflows__ in the sidebar and click on the "Translation Hub" workflow.';

/**
 * Tutorial values used for workflow/agent naming and registration.
 */
export const AGENTS_AS_TOOLS_TUTORIAL_VALUES = {
  orchestration: {
    name: 'Translation Hub',
    description:
      'An orchestrator agent that delegates translation tasks to specialized tool agents.',
  },
  agents: {
    spanish: {
      name: 'Spanish Translator',
      instructions:
        'You translate the user\'s message to Spanish. Always respond with only the Spanish translation, nothing else.',
    },
    french: {
      name: 'French Translator',
      instructions:
        'You translate the user\'s message to French. Always respond with only the French translation, nothing else.',
    },
    orchestrator: {
      name: 'Translation Orchestrator',
      instructions:
        'You are a translation orchestrator. When a user asks for translations, use your tools to translate. If the user asks for Spanish, use the translate_to_spanish tool. If the user asks for French, use the translate_to_french tool. If they ask for both, call both tools. Never translate on your own - always use the provided tools. Present the results clearly labeled by language.',
    },
  },
};

/**
 * Agents as Tools Tutorial Steps
 *
 * Step Flow:
 * 1-5:   Create tool agents on Agents page
 * 6-8:   Navigate to Workflows, create workflow
 * 9-11:  Add User Prompt step
 * 12-15: Add Orchestrator Agent inline (click +, pick Agent - Create, create with tool attachment, add step)
 * 16-18: Run, submit input, and view output
 */
export const AGENTS_AS_TOOLS_TUTORIAL_STEPS: TutorialStep[] = [
  // --- Phase 1: Create Tool Agents (Steps 1-5) ---
  {
    stepNumber: 1,
    targetSelector: '[data-tutorial="nav-agents"]',
    title: 'Welcome to Agents as Tools!',
    description:
      "In this tutorial, you'll learn the __agents as tools__ pattern - where specialized agents are used as tools by an orchestrator agent.\n\n" +
      "You'll create two translator agents (Spanish and French), then build an orchestrator that delegates to them automatically.\n\n" +
      'This is a powerful pattern for building modular AI workflows where each agent has a focused role.',
    actionPrompt: "Click 'Agents' in the sidebar to get started.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-agents"]',
    },
    expectedRoute: '/projects/:id',
  },
  {
    stepNumber: 2,
    targetSelector: '[data-tutorial="btn-create-agent"]',
    title: 'Create the Spanish Translator',
    description:
      'First, create a specialized translator agent for Spanish. This agent will later be used as a __tool__ by an orchestrator.\n\n' +
      'Click __New Agent__, then select __Create from Scratch__.',
    actionPrompt: "Click 'New Agent', then select 'Create from Scratch'.",
    position: 'left',
    tooltipMaxWidth: 280,
    tooltipOffsetX: -120,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-from-scratch"]',
    },
    expectedRoute: '/agents',
  },
  {
    stepNumber: 3,
    targetSelector: '[data-tutorial="form-agent"]',
    title: 'Configure the Spanish Translator',
    description:
      'Fill in the agent details:\n\n' +
      '1. Agent Name: **Spanish Translator**\n\n' +
      '2. Instructions:\n' +
      "[[copy:Instructions:You translate the user's message to Spanish. Always respond with only the Spanish translation, nothing else.]]\n\n" +
      'Leave all other settings at their defaults.',
    actionPrompt: "Fill in the fields, then click 'Create Agent'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-agent"]',
    },
    resumeFallbackStep: 2,
  },
  {
    stepNumber: 4,
    targetSelector: '[data-tutorial="btn-create-agent"]',
    title: 'Create the French Translator',
    description:
      'Great! Now create the second tool agent for French translation.\n\n' +
      'Click __New Agent__, then select __Create from Scratch__.',
    actionPrompt: "Click 'New Agent', then select 'Create from Scratch'.",
    position: 'left',
    tooltipMaxWidth: 280,
    tooltipOffsetX: -120,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-from-scratch"]',
    },
    expectedRoute: '/agents',
  },
  {
    stepNumber: 5,
    targetSelector: '[data-tutorial="form-agent"]',
    title: 'Configure the French Translator',
    description:
      'Fill in the agent details:\n\n' +
      '1. Agent Name: **French Translator**\n\n' +
      '2. Instructions:\n' +
      "[[copy:Instructions:You translate the user's message to French. Always respond with only the French translation, nothing else.]]\n\n" +
      'Leave all other settings at their defaults.',
    actionPrompt: "Fill in the fields, then click 'Create Agent'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-agent"]',
    },
    resumeFallbackStep: 4,
  },

  // --- Phase 2: Create Workflow (Steps 6-8) ---
  {
    stepNumber: 6,
    targetSelector: '[data-tutorial="nav-workflows"]',
    title: 'Build the Orchestrator Workflow',
    description:
      'Both translator agents are ready! Now we\'ll create a workflow with an __orchestrator agent__ that uses them as tools.\n\n' +
      'The orchestrator will decide which translator to call based on the user\'s request.',
    actionPrompt: "Click 'Workflows' in the sidebar to continue.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-workflows"]',
    },
  },
  {
    stepNumber: 7,
    targetSelector: '[data-tutorial="btn-create-workflow"]',
    title: 'Create the Translation Hub',
    description: "Let's create the workflow that ties everything together.",
    actionPrompt: "Click 'New Workflow' to create your workflow.",
    position: 'bottom',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-workflow"]',
    },
    expectedRoute: '/workflows',
  },
  {
    stepNumber: 8,
    targetSelector: '[data-tutorial="form-workflow"]',
    title: 'Name Your Workflow',
    description:
      'Set up the workflow details:\n\n' +
      '1. For Name, enter: **Translation Hub**\n\n' +
      '2. For Description, enter: **An orchestrator agent that delegates translation tasks to specialized tool agents.**',
    actionPrompt: "Enter the details and click 'Create' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-workflow"]',
    },
    resumeFallbackStep: 7,
  },

  // --- Phase 3: Add User Prompt Step (Steps 9-11) ---
  {
    stepNumber: 9,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add User Prompt',
    description:
      'Welcome to the workflow editor! First, we need a way for users to provide text to translate.\n\n' +
      'Click the __+__ button below the __Start__ node to add a step.',
    actionPrompt: 'Click the __+__ button below the Start node.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
  },
  {
    stepNumber: 10,
    targetSelector: '[data-tutorial="step-type-user_prompt"]',
    title: 'Select User Prompt',
    description:
      'Select the __User Prompt__ step type. This will ask the user for text to translate.',
    actionPrompt: 'Click on __User Prompt__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-user_prompt"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
    resumeFallbackStep: 9,
  },
  {
    stepNumber: 11,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure User Prompt',
    description:
      'Configure the User Prompt:\n\n' +
      '__Label__: **Get Translation Request**\n\n' +
      "__Input Message__: **Enter text to translate and specify the target language(s) - Spanish, French, or both.**",
    actionPrompt: "Fill in the fields, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
    resumeFallbackStep: 9,
  },

  // --- Phase 4: Add Orchestrator Agent with Tool Agents (Steps 12-15) ---
  {
    stepNumber: 12,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add the Orchestrator Agent',
    description:
      "Now we'll add the orchestrator - the agent that decides which translator tools to call.\n\n" +
      'Click the __+__ button below the User Prompt step.',
    actionPrompt: 'Click the __+__ button below the User Prompt step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
  },
  {
    stepNumber: 13,
    targetSelector: '[data-tutorial="step-option-run_agent-create"]',
    title: 'Select Agent - Create',
    description:
      "The Step Type picker has appeared. The __Translation Orchestrator__ will __generate__ a delegated translation by calling its tool agents, so we'll use the __Create__ intent.",
    actionPrompt: 'Click on __Agent - Create__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-create"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
    resumeFallbackStep: 12,
  },
  {
    stepNumber: 14,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Create the Translation Orchestrator',
    description:
      'Now create the orchestrator agent and attach the translator tools. Follow these steps carefully:\n\n' +
      '1. Click the __+__ next to the agent dropdown, then __Create from Scratch__\n' +
      '2. Name: **Translation Orchestrator**\n' +
      '3. Instructions:\n' +
      "[[copy:Instructions:You are a translation orchestrator. When a user asks for translations, use your tools to translate. If the user asks for Spanish, use the translate_to_spanish tool. If the user asks for French, use the translate_to_french tool. If they ask for both, call both tools. Never translate on your own - always use the provided tools. Present the results clearly labeled by language.]]\n" +
      '4. Expand the __Tools__ section (click the arrow next to "Tools")\n' +
      '5. Click __Add Tool Agent__ (purple button)\n' +
      '6. Select __Spanish Translator__, set Tool Name to **translate_to_spanish** and Description to **Translate the user\'s message to Spanish**, then click __Add Tool__\n' +
      '7. Click __Add Tool Agent__ again\n' +
      '8. Select __French Translator__, set Tool Name to **translate_to_french** and Description to **Translate the user\'s message to French**, then click __Add Tool__\n' +
      '9. Click __Create Agent__\n\n' +
      "Click __Continue__ when the agent is created.",
    actionPrompt: 'Create the orchestrator agent with both tool agents attached.',
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
    resumeFallbackStep: 12,
  },
  {
    stepNumber: 15,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Add the Orchestrator Step',
    description:
      'The __Translation Orchestrator__ should now be selected as the agent for this step.\n\n' +
      "If you haven't attached the tool agents yet, click __Edit Agent__ in the agent details to open the agent form and add them.\n\n" +
      "Click __Add Step__ to add the orchestrator to your workflow.",
    actionPrompt: "Click 'Add Step' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
    resumeFallbackStep: 12,
  },

  // --- Phase 5: Run and View Output (Steps 16-18) ---
  {
    stepNumber: 16,
    targetSelector: '[data-tutorial="btn-run-workflow"]',
    title: 'Run Your Translation Hub!',
    description:
      'Your Translation Hub is ready! When you run it:\n\n' +
      '- It asks for text and a target language\n' +
      '- The orchestrator decides which tool agent(s) to call\n' +
      '- The translator agents produce the translations\n' +
      '- Results are returned to you',
    actionPrompt: "Click 'Execute' to run your workflow!",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-run-workflow"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
  },
  {
    stepNumber: 17,
    targetSelector: '[data-tutorial="btn-submit-user-input"]',
    title: 'Try a Translation!',
    description:
      "Type a message and specify the language(s). Try:\n\n" +
      "**Translate 'Hello, how are you?' to both Spanish and French**\n\n" +
      'The orchestrator will automatically call the right tool agent(s) based on your request.',
    actionPrompt: "Enter your text and click 'Submit'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-user-input"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
  },
  {
    stepNumber: 18,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'Translation Complete!',
    description:
      'Your workflow is running! Once complete, check out __View Final Output__ to see the translations.\n\n' +
      'Notice how the orchestrator automatically chose the right tool agent(s) based on your request.\n\n' +
      "__What you've learned:__\n" +
      '- Creating __specialized agents__ that serve as tools\n' +
      '- Using __Add Tool Agent__ to attach agents as tools to an orchestrator\n' +
      '- Configuring __tool names__ and __descriptions__ so the orchestrator knows when to call each tool\n' +
      '- The __agents-as-tools pattern__ where an orchestrator delegates to specialized agents\n\n' +
      '__Tip:__ This is a reusable building block. On the Workflows page, click the __Duplicate__ icon ' +
      'to copy the Translation Hub into another project - the Spanish, French, and orchestrator agents ' +
      'travel with it. Or, from the Projects page, click the __Clone__ icon on the project card to ' +
      'deep-copy this whole project (agents, models, queues, and workflows) into a new sandbox.\n\n' +
      "Click __Finish Tutorial__ when you're ready!",
    actionPrompt: "View the output, then click 'Finish Tutorial'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: AGENTS_AS_TOOLS_ROUTE_MSG,
  },
];
